'use client'

import { useMemo, useRef, useState, useCallback, useEffect } from 'react'
import { useFrame, useThree, ThreeEvent } from '@react-three/fiber'
import * as THREE from 'three'

// LOD Thresholds: [Distance to START showing tier]
// Adjusted to prevent "solid orange" look
const LOD_THRESHOLDS = [
	100.0, // Tier 0: Always visible
	4.0, // Tier 1: 5K (Show closer)
	3.0, // Tier 2: 20K
	2.4, // Tier 3: 50K
	1.8, // Tier 4: 100K
	1.5, // Tier 5: 250K
	1.3, // Tier 6: 500K
	1.1 // Tier 7: 1M (Show ONLY when right on top)
]

const vertexShader = `
	uniform float uPointSize;
  uniform float uLodThresholds[8];
  uniform float uCameraDistance;
  uniform float uMix; // 0.0 = Sphere, 1.0 = Grid
  uniform float uSelectedIndex; // -1.0 = none selected (legacy, for single selection)
  uniform float uSelectedIndices[32]; // Array of selected indices for multi-select
  uniform float uSelectedCount; // Number of selected indices
  uniform float uTime;
  uniform float uMaxHeight; // Current blockchain height
  uniform float uFilterMode; // 0.0 = show-all, 1.0 = for-sale, 2.0 = my-lands
  
  attribute float aIndex;
  attribute float aLod;
  attribute float aIsForSale;
  attribute float aIsOwned;
  attribute float aIsLowPrice;
  
  varying vec3 vPosition;
  varying float vAlpha;
  varying float vIsForSale;
  varying float vIsOwned;
  varying float vIsLowPrice;
  varying float vIsSelected;
  varying float vMix;
  varying float vTime;
  varying float vIsMined; // 1.0 if block is mined, 0.0 if not
  varying float vFilterMode;
  
  void main() {
    vMix = uMix;
    vTime = uTime;
    // Check if this block has been mined
    vIsMined = (aIndex < uMaxHeight) ? 1.0 : 0.0;
    
    // Check if this point is selected (multi-select support)
    vIsSelected = 0.0;
    // Check legacy single selection first
    if (abs(aIndex - uSelectedIndex) < 0.5) {
      vIsSelected = 1.0;
    }
    // Check multi-select array
    for (int i = 0; i < 32; i++) {
      if (float(i) >= uSelectedCount) break;
      if (abs(aIndex - uSelectedIndices[i]) < 0.5) {
        vIsSelected = 1.0;
        break;
      }
    }
    
    // --- Grid Logic ---
    // Calculate grid position from index
    float col = mod(aIndex, 1000.0);
    float row = floor(aIndex / 1000.0);
    
    // Scale to match typical globe size (-2.0 to 2.0 range)
    // 1000 points * 0.004 = 4.0 units wide
    // Block #0 at top-left, #1 to the right, rows go down
    float gridX = (col - 500.0) * 0.004;
    float gridY = (500.0 - row) * 0.004;  // Flip Y so row 0 is at top
    vec3 gridPos = vec3(gridX, gridY, 0.0);
    
    // Interpolate between sphere position and grid position
    vec3 mixedPos = mix(position, gridPos, uMix);
    
    vPosition = mixedPos;
    vIsForSale = aIsForSale;
    vIsOwned = aIsOwned;
    vIsLowPrice = aIsLowPrice;
    vFilterMode = uFilterMode;
    
    // --- LOD Logic (Auto Only) ---
    int tier = int(aLod);
    float threshold = 0.0;
    
    if (tier == 0) threshold = uLodThresholds[0];
    else if (tier == 1) threshold = uLodThresholds[1];
    else if (tier == 2) threshold = uLodThresholds[2];
    else if (tier == 3) threshold = uLodThresholds[3];
    else if (tier == 4) threshold = uLodThresholds[4];
    else if (tier == 5) threshold = uLodThresholds[5];
    else if (tier == 6) threshold = uLodThresholds[6];
    else if (tier == 7) threshold = uLodThresholds[7];
    
    // Smooth fade window
    float fadeStart = threshold + 0.5;
    float fadeEnd = threshold;
    
    float alpha = smoothstep(fadeStart, fadeEnd, uCameraDistance);
    if (tier == 0) alpha = 1.0;
    
    // Always show selected point regardless of LOD
    if (vIsSelected > 0.5) alpha = 1.0;
    
    // In Grid mode, show ALL points - no LOD culling
    // This ensures the full grid is visible when zoomed in
    if (uMix > 0.5) alpha = 1.0;
    
    if (alpha < 0.01) {
        gl_Position = vec4(0.0, 0.0, -9999.0, 1.0);
        gl_PointSize = 0.0;
        return;
    }
    
    vAlpha = alpha;
    
    vec4 mvPosition = modelViewMatrix * vec4(mixedPos, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    
    // Size attenuation with sharper scaling for close-up views
    float distScale = smoothstep(1.0, 3.0, uCameraDistance); 
    
    float baseSize;
    if (uMix > 0.5) {
        // Grid mode: fill cells completely when zoomed out, show gaps when close
        // At distance 0.5+, cells should be touching (no gaps)
        // At distance 0.15 or less, show small squares with gaps
        float gridFillFactor = smoothstep(0.15, 0.5, uCameraDistance);
        // Scale from 0.6 (gaps visible) to 1.3 (cells overlap slightly to prevent aliasing)
        float cellFill = mix(0.6, 1.3, gridFillFactor);
        baseSize = uPointSize * cellFill * (3.0 / -mvPosition.z);
    } else {
        // Globe mode: shrink points when close to prevent overlap
        float densityFactor = 0.3 + (0.7 * distScale);
        baseSize = uPointSize * densityFactor * (3.0 / -mvPosition.z);
    }
    
    // Make selected point larger
    gl_PointSize = vIsSelected > 0.5 ? baseSize * 1.5 : baseSize;
  }
`

const fragmentShader = `
  varying float vAlpha;
  varying float vIsForSale;
  varying float vIsOwned;
  varying float vIsLowPrice;
  varying float vIsSelected;
  varying float vMix;
  varying float vTime;
  varying float vIsMined;
  varying float vFilterMode;
  
  void main() {
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    
    // Grid mode = squares, Globe mode = circles
    if (vMix < 0.5) {
      // Circle shape for globe mode
      if (dist > 0.5) discard;
    }
    // Squares: no discard needed, gl_PointCoord already covers square area
    
    // Crisper edge (less fuzzy) - only for circles
    float shapeAlpha = vMix > 0.5 ? 1.0 : (1.0 - smoothstep(0.45, 0.5, dist));
    
    float finalAlpha = shapeAlpha * vAlpha;
    
    if (finalAlpha < 0.1) discard;
    
    // Unmined blocks are dark/black
    if (vIsMined < 0.5) {
        gl_FragColor = vec4(0.1, 0.1, 0.1, finalAlpha * 0.3);
        return;
    }
    
    // Determine if this block should be highlighted based on filter mode
    // 0.0 = show-all, 1.0 = for-sale, 2.0 = my-lands
    bool isHighlighted = true;
    if (vFilterMode > 0.5 && vFilterMode < 1.5) {
        // for-sale mode: only highlight blocks that are for sale
        isHighlighted = vIsForSale > 0.5;
    } else if (vFilterMode > 1.5) {
        // my-lands mode: only highlight blocks that are owned
        isHighlighted = vIsOwned > 0.5;
    }
    
    vec3 color = vec3(1.0, 0.55, 0.0); // Default Orange
    
    // Blue for owned bitmaps (my lands) - #178EFE
    if (vIsOwned > 0.5) {
        color = vec3(0.09, 0.56, 1.0); // Blue #178EFE
    }
    
    // Green for "For Sale" items (takes precedence over owned)
    // Use lighter green for blocks under $20
    if (vIsForSale > 0.5) {
        if (vIsLowPrice > 0.5) {
            color = vec3(0.6, 1.0, 0.6); // Lighter green for blocks under $20
        } else {
            color = vec3(0.0, 1.0, 0.0); // Bright Green
        }
    }
    
    // Grey out non-highlighted blocks when filter is active
    if (!isHighlighted && vFilterMode > 0.5) {
        color = vec3(0.2, 0.2, 0.2); // Dark grey
        finalAlpha *= 0.4; // More transparent
    }
    
    // White pulsing selection effect
    if (vIsSelected > 0.5) {
        // Pulse effect: oscillate between 0.7 and 1.0 brightness
        float pulse = 0.85 + 0.15 * sin(vTime * 4.0);
        
        if (vMix > 0.5) {
            // Grid mode: entire square pulses white
            color = vec3(pulse);
            finalAlpha = 1.0;
        } else {
            // Globe mode: white ring with pulse
            float ringInner = 0.28;
            if (dist > ringInner) {
                // Outer ring - pulsing white
                color = vec3(pulse);
                finalAlpha = 1.0;
            } else {
                // Inner keeps original color but brightened
                color = mix(color, vec3(1.0), 0.3);
            }
        }
        finalAlpha = max(finalAlpha, 0.95);
    }
    
    gl_FragColor = vec4(color, finalAlpha);
  }
`

type FilterMode = 'show-all' | 'for-sale' | 'my-lands'
type ViewMode = 'globe' | 'grid'

interface PointsGlobeProps {
	radius?: number
	pointSize?: number
	autoRotate?: boolean
	selectedIndex?: number
	selectedIndices?: number[] // For multi-select
	onPointClick?: (index: number, shiftKey?: boolean) => void
	onLodChange?: (tier: number, distance: number) => void
	viewMode?: ViewMode
	listedBlocks?: Set<number>
	ownedBlocks?: Set<number>
	lowPriceBlocks?: Set<number>
	filterMode?: FilterMode
	maxHeight?: number
}

const EMPTY_SELECTED_INDICES: number[] = []

function configurePointsRaycaster(raycaster: THREE.Raycaster, viewMode: ViewMode) {
	raycaster.params.Points = { threshold: viewMode === 'grid' ? 0.02 : 0.003 }
}

function setMaterialUniform<T>(material: THREE.ShaderMaterial, uniformName: string, value: T) {
	material.uniforms[uniformName].value = value
}

function getSelectedUniformIndices(selectedIndices: number[]) {
	return Array.from({ length: 32 }, (_, index) => selectedIndices[index] ?? -1.0)
}

function getFilterModeValue(filterMode: FilterMode) {
	if (filterMode === 'show-all') return 0.0
	if (filterMode === 'for-sale') return 1.0
	return 2.0
}

function syncGroupRotation(
	group: THREE.Group,
	viewMode: ViewMode,
	autoRotate: boolean,
	delta: number
) {
	if (viewMode === 'grid') {
		group.rotation.x = THREE.MathUtils.lerp(group.rotation.x, 0, delta * 3)
		group.rotation.y = THREE.MathUtils.lerp(group.rotation.y, 0, delta * 3)
		group.rotation.z = THREE.MathUtils.lerp(group.rotation.z, 0, delta * 3)
	} else if (autoRotate) {
		group.rotation.y += delta * 0.1
	}
}

function syncFrameUniforms(
	material: THREE.ShaderMaterial,
	viewMode: ViewMode,
	elapsedTime: number,
	camera: THREE.Camera,
	delta: number
) {
	setMaterialUniform(material, 'uTime', elapsedTime)

	const targetMix = viewMode === 'grid' ? 1.0 : 0.0
	if (viewMode === 'globe') {
		const nextMix = THREE.MathUtils.lerp(material.uniforms.uMix.value, targetMix, delta * 2.0)
		setMaterialUniform(material, 'uMix', nextMix)
	} else {
		setMaterialUniform(material, 'uMix', targetMix)
	}

	const distance = viewMode === 'grid' ? Math.abs(camera.position.z) : camera.position.length()
	setMaterialUniform(material, 'uCameraDistance', distance)
	return distance
}

export function PointsGlobe({
	radius = 1,
	pointSize = 2.0,
	autoRotate = false,
	selectedIndex = -1,
	selectedIndices = EMPTY_SELECTED_INDICES,
	viewMode = 'globe',
	listedBlocks,
	ownedBlocks,
	lowPriceBlocks,
	filterMode = 'show-all',
	onPointClick,
	onLodChange,
	maxHeight = 1000000
}: PointsGlobeProps) {
	const groupRef = useRef<THREE.Group>(null)
	const pointsRef = useRef<THREE.Points>(null)
	const { camera, raycaster } = useThree()

	const [isLoaded, setIsLoaded] = useState(false)
	const [geometry, setGeometry] = useState<THREE.BufferGeometry | null>(null)

	// Drag detection - track pointer down position to distinguish clicks from drags
	const pointerDownPos = useRef<{ x: number; y: number } | null>(null)
	const DRAG_THRESHOLD = 5 // pixels - movement below this is considered a click

	const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
		pointerDownPos.current = { x: event.nativeEvent.clientX, y: event.nativeEvent.clientY }
	}, [])

	const isDragNotClick = useCallback((event: ThreeEvent<MouseEvent>): boolean => {
		if (!pointerDownPos.current) return false
		const dx = event.nativeEvent.clientX - pointerDownPos.current.x
		const dy = event.nativeEvent.clientY - pointerDownPos.current.y
		const distance = Math.sqrt(dx * dx + dy * dy)
		return distance > DRAG_THRESHOLD
	}, [])

	// Configure raycaster for points - threshold determines how close a click needs to be
	// Grid mode needs larger threshold since points are transformed by shader
	useEffect(() => {
		configurePointsRaycaster(raycaster, viewMode)
	}, [raycaster, viewMode])

	// Worker initialization
	useEffect(() => {
		const worker = new Worker(new URL('../workers/point-generation.worker.ts', import.meta.url))

		worker.onmessage = (e) => {
			const { positions, lods, indices, isForSale, isOwned, isLowPrice } = e.data

			const geom = new THREE.BufferGeometry()
			geom.setAttribute('position', new THREE.BufferAttribute(positions, 3))
			geom.setAttribute('aIndex', new THREE.BufferAttribute(indices, 1))
			geom.setAttribute('aLod', new THREE.BufferAttribute(lods, 1))
			geom.setAttribute('aIsForSale', new THREE.BufferAttribute(isForSale, 1))
			geom.setAttribute('aIsOwned', new THREE.BufferAttribute(isOwned, 1))
			geom.setAttribute('aIsLowPrice', new THREE.BufferAttribute(isLowPrice, 1))
			geom.computeBoundingSphere()

			setGeometry(geom)
			setIsLoaded(true)
		}

		worker.postMessage({ maxPoints: 1000000, radius })

		return () => worker.terminate()
	}, [radius])

	// Update isForSale attribute when listedBlocks changes
	useEffect(() => {
		if (!geometry || !listedBlocks) return

		const isForSaleAttr = geometry.getAttribute('aIsForSale') as THREE.BufferAttribute
		if (!isForSaleAttr) return

		const array = isForSaleAttr.array as Float32Array

		// Mark only actual for-sale blocks
		for (let i = 0; i < array.length; i++) {
			array[i] = listedBlocks.has(i) ? 1.0 : 0.0
		}

		isForSaleAttr.needsUpdate = true
	}, [geometry, listedBlocks])

	// Update isOwned attribute when ownedBlocks changes
	useEffect(() => {
		if (!geometry || !ownedBlocks) return

		const isOwnedAttr = geometry.getAttribute('aIsOwned') as THREE.BufferAttribute
		if (!isOwnedAttr) return

		const array = isOwnedAttr.array as Float32Array

		// Mark only actual owned blocks
		for (let i = 0; i < array.length; i++) {
			array[i] = ownedBlocks.has(i) ? 1.0 : 0.0
		}

		isOwnedAttr.needsUpdate = true
	}, [geometry, ownedBlocks])

	// Update isLowPrice attribute when lowPriceBlocks changes
	useEffect(() => {
		if (!geometry) return

		const isLowPriceAttr = geometry.getAttribute('aIsLowPrice') as THREE.BufferAttribute
		if (!isLowPriceAttr) return

		const array = isLowPriceAttr.array as Float32Array

		// Mark blocks that are under $20
		for (let i = 0; i < array.length; i++) {
			array[i] = lowPriceBlocks?.has(i) ? 1.0 : 0.0
		}

		isLowPriceAttr.needsUpdate = true
	}, [geometry, lowPriceBlocks])

	const material = useMemo(
		() =>
			new THREE.ShaderMaterial({
				vertexShader,
				fragmentShader,
				transparent: true,
				depthWrite: true, // ENABLED: Prevents accumulation/clumping artifacts
				blending: THREE.NormalBlending,
				uniforms: {
					uPointSize: { value: pointSize },
					uCameraDistance: { value: 10.0 },
					uLodThresholds: { value: LOD_THRESHOLDS },
					uMix: { value: 1.0 }, // Start in grid mode
					uSelectedIndex: { value: -1.0 },
					uSelectedIndices: { value: new Array(32).fill(-1.0) }, // Multi-select array
					uSelectedCount: { value: 0.0 },
					uTime: { value: 0.0 },
					uMaxHeight: { value: 1000000.0 },
					uFilterMode: { value: 0.0 } // 0 = show-all, 1 = for-sale, 2 = my-lands
				}
			}),
		[pointSize]
	)

	// Sync props to uniforms
	useEffect(() => {
		setMaterialUniform(material, 'uPointSize', pointSize)
	}, [pointSize, material])

	// Sync selected index to uniform (legacy single selection)
	useEffect(() => {
		setMaterialUniform(material, 'uSelectedIndex', selectedIndex)
	}, [selectedIndex, material])

	// Sync selected indices array for multi-select
	useEffect(() => {
		setMaterialUniform(material, 'uSelectedIndices', getSelectedUniformIndices(selectedIndices))
		setMaterialUniform(material, 'uSelectedCount', Math.min(selectedIndices.length, 32))
	}, [selectedIndices, material])

	// Sync maxHeight to uniform
	useEffect(() => {
		setMaterialUniform(material, 'uMaxHeight', maxHeight)
	}, [maxHeight, material])

	// Sync filterMode to uniform
	useEffect(() => {
		setMaterialUniform(material, 'uFilterMode', getFilterModeValue(filterMode))
	}, [filterMode, material])

	// Performant click handler using raycasting
	const handleClick = useCallback(
		(event: ThreeEvent<MouseEvent>) => {
			if (!onPointClick || !pointsRef.current || !geometry) return

			// Ignore if this was a drag, not a click
			if (isDragNotClick(event)) {
				console.log('[Click] Ignored - detected as drag/pan')
				return
			}

			const startTime = performance.now()

			// For grid mode, calculate grid cell from click position on z=0 plane
			if (viewMode === 'grid') {
				// Get the click point on the z=0 plane
				const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
				const clickPoint = new THREE.Vector3()
				event.ray.intersectPlane(plane, clickPoint)

				if (clickPoint) {
					// Convert world position back to grid index
					// Grid formula: gridX = (col - 500) * 0.004, gridY = (row - 500) * 0.004
					// Reverse: col = (gridX / 0.004) + 500, row = (gridY / 0.004) + 500
					const col = Math.round(clickPoint.x / 0.004 + 500)
					const row = Math.round(clickPoint.y / 0.004 + 500)

					// Calculate index: index = row * 1000 + col
					const bitmapIndex = row * 1000 + col

					// Validate bounds (0 to maxHeight - unmined blocks not clickable)
					if (
						bitmapIndex >= 0 &&
						bitmapIndex < maxHeight &&
						col >= 0 &&
						col < 1000 &&
						row >= 0 &&
						row < 1000
					) {
						console.log(
							`[GridSelect] Clicked col=${col}, row=${row}, index=${bitmapIndex}, shift=${event.nativeEvent.shiftKey}`
						)
						onPointClick(bitmapIndex, event.nativeEvent.shiftKey)
					} else if (bitmapIndex >= maxHeight) {
						console.log(`[GridSelect] Block ${bitmapIndex} not yet mined (height: ${maxHeight})`)
					} else {
						console.log(`[GridSelect] Click outside grid bounds: col=${col}, row=${row}`)
					}
				}
				return
			}

			// Globe mode: use raycasting
			// Get all intersections with our points object
			const pointIntersects = event.intersections.filter(
				(i) => i.object === pointsRef.current && i.index !== undefined
			)

			const raycastTime = performance.now()
			console.log(
				`[RaySelect] Raycast time: ${(raycastTime - startTime).toFixed(2)}ms, found ${
					pointIntersects.length
				} hits`
			)

			if (pointIntersects.length === 0) {
				console.log(`[RaySelect] No intersections found`)
				return
			}

			// Sort by distanceToRay (closest to click position) - this picks the point nearest to cursor
			// For points with similar distanceToRay, prefer closer to camera (front-facing)
			pointIntersects.sort((a, b) => {
				const aDistToRay = a.distanceToRay ?? Infinity
				const bDistToRay = b.distanceToRay ?? Infinity
				// If distanceToRay is very similar (within threshold), sort by camera distance
				if (Math.abs(aDistToRay - bDistToRay) < 0.001) {
					return a.distance - b.distance
				}
				return aDistToRay - bDistToRay
			})

			// Log top candidates for debugging
			const indexAttr = geometry.getAttribute('aIndex')
			console.log(`[RaySelect] Top 3 candidates:`)
			pointIntersects.slice(0, 3).forEach((hit, i) => {
				const idx = indexAttr ? Math.floor(indexAttr.getX(hit.index!)) : hit.index
				console.log(
					`  ${i + 1}. bitmap=${idx}, dist=${hit.distance.toFixed(3)}, distToRay=${
						hit.distanceToRay?.toFixed(4) ?? 'N/A'
					}`
				)
			})

			// Pick the closest point to the camera
			const pointIntersect = pointIntersects[0]

			// Get the actual bitmap index from the aIndex attribute
			const bitmapIndex = indexAttr
				? Math.floor(indexAttr.getX(pointIntersect.index!))
				: pointIntersect.index!

			// Check if block is mined
			if (bitmapIndex >= maxHeight) {
				console.log(`[RaySelect] Block ${bitmapIndex} not yet mined (height: ${maxHeight})`)
				return
			}

			const endTime = performance.now()
			console.log(
				`[RaySelect] Selected bitmap: ${bitmapIndex}, total time: ${(endTime - startTime).toFixed(
					2
				)}ms`
			)

			onPointClick(bitmapIndex, event.nativeEvent.shiftKey)
		},
		[onPointClick, geometry, viewMode, maxHeight, isDragNotClick]
	)

	// Animation Loop
	useFrame((state, delta) => {
		if (groupRef.current) {
			syncGroupRotation(groupRef.current, viewMode, autoRotate, delta)
		}

		const dist = syncFrameUniforms(material, viewMode, state.clock.elapsedTime, camera, delta)

		// Determine current tier for UI
		if (onLodChange) {
			let tier = 0
			for (let i = 7; i >= 0; i--) {
				if (dist <= LOD_THRESHOLDS[i]) {
					tier = i
					break
				}
			}
			onLodChange(tier, dist)
		}
	})

	// Grid click handler for the invisible plane
	const handleGridPlaneClick = useCallback(
		(event: ThreeEvent<MouseEvent>) => {
			if (!onPointClick) return

			// Ignore if this was a drag, not a click
			if (isDragNotClick(event)) {
				console.log('[GridPlaneClick] Ignored - detected as drag/pan')
				return
			}

			event.stopPropagation()

			const clickPoint = event.point

			// Convert world position back to grid index
			// Grid formula: gridX = (col - 500) * 0.004, gridY = (500 - row) * 0.004
			// Reverse: col = (gridX / 0.004) + 500, row = 500 - (gridY / 0.004)
			// Use floor with 0.5 offset to properly center the cell detection
			const col = Math.floor(clickPoint.x / 0.004 + 500.5)
			const row = Math.floor(500.5 - clickPoint.y / 0.004)

			// Calculate index: index = row * 1000 + col
			const bitmapIndex = row * 1000 + col

			// Validate bounds (0 to maxHeight - unmined blocks not clickable)
			if (
				bitmapIndex >= 0 &&
				bitmapIndex < maxHeight &&
				col >= 0 &&
				col < 1000 &&
				row >= 0 &&
				row < 1000
			) {
				console.log(
					`[GridPlaneClick] col=${col}, row=${row}, index=${bitmapIndex}, click=(${clickPoint.x.toFixed(
						3
					)}, ${clickPoint.y.toFixed(3)}), shift=${event.nativeEvent.shiftKey}`
				)
				onPointClick(bitmapIndex, event.nativeEvent.shiftKey)
			} else if (bitmapIndex >= maxHeight) {
				console.log(`[GridPlaneClick] Block ${bitmapIndex} not yet mined (height: ${maxHeight})`)
			} else {
				console.log(
					`[GridPlaneClick] Outside bounds: col=${col}, row=${row}, click=(${clickPoint.x.toFixed(
						3
					)}, ${clickPoint.y.toFixed(3)})`
				)
			}
		},
		[onPointClick, maxHeight, isDragNotClick]
	)

	if (!isLoaded || !geometry) return null

	return (
		<>
			{/* Invisible click plane for grid mode - outside group to avoid rotation issues */}
			{viewMode === 'grid' && (
				<mesh position={[0, 0, 0]} onPointerDown={handlePointerDown} onClick={handleGridPlaneClick}>
					<planeGeometry args={[100, 100]} />
					<meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} />
				</mesh>
			)}
			<group ref={groupRef}>
				<points
					ref={pointsRef}
					geometry={geometry}
					material={material}
					frustumCulled={false}
					onPointerDown={handlePointerDown}
					onClick={handleClick}
				/>
			</group>
		</>
	)
}
